import React, { useEffect, useState } from 'react'
import ImmutablePropTypes from 'react-immutable-proptypes'
import PropTypes from 'prop-types'
import compose from 'recompose/compose'
import { connect as connectRedux } from 'react-redux'
import _replace from 'lodash/replace'
import _get from 'lodash/get'
import { List, Map, fromJS } from 'immutable'
import { useLazyQuery } from '@apollo/client'
import { connect as connectStaticText } from '../../StaticText/connect'
import { getDateTime } from '../../../services/date-time/index'
import { renderSymbol, EUR, GBP } from '../../../services/currency/index'
import {
  showChangePlanUpgradeMessage,
  showReactivateMessage,
  showFmtvReactivateMessage,
  userHasPaymentError,
  showUpdatePaymentMethodMessage,
  showFreeTrialUpgradeMessage,
  showFreeTrialEndingMessage,
} from '../../../services/user-account/index'
import MyAccountMessage,
{
  MESSAGE_TYPE_CHANGE,
  MESSAGE_TYPE_REACTIVATE,
  MESSAGE_TYPE_ERROR,
  MESSAGE_TYPE_UPDATE,
  MESSAGE_TYPE_PROMO,
  MESSAGE_TYPE_TRIAL_UPGRADE,
  MESSAGE_TYPE_TRIAL_ENDING,
} from '../MyAccountMessage/index'
import {
  URL_CART_ACCOUNT_CREATION_CREATE,
  URL_ACCOUNT_CREATION,
  URL_ACCOUNT_CHANGE_PLAN,
  URL_ACCOUNT,
} from '../../../services/url/constants'
import {
  PLAN_SUBSCRIPTION_ANNUAL,
  PLAN_SUBSCRIPTION_MONTHLY,
  annualPlanPercentSavings,
  getUserAuthPlanList,
} from '../../../services/plans/index'
import { get as getConfig } from '../../../config/index'
import { getPrimary } from '../../../services/languages/index'
import {
  formatBillingPlans,
  BILLING_PLAN_GRID_LOCATION_CHANGE_PLAN,
} from '../../../services/plans/utils'
import { GET_PLANS } from '../../../services/plans/query'
import { isWinbackFlagEnabled } from '../../../services/winback/index'

const config = getConfig()
const planGraphQLActive = !!_get(config, ['features', 'checkout', 'planGraphQL'])

export const top = 'top'
export const bottom = 'bottom'

function getClassName () {
  return 'my-account-messages'
}

function getPricingVariable (planType, plans, userAccount) {
  // price increase
  const userPlanSkuList = getUserAuthPlanList(plans, userAccount)
  const monthlyPlanSku = userPlanSkuList.get(0)
  const annualPlanSku = userPlanSkuList.get(1)

  const annualPlan = (plans.getIn(['data', 'plans']) || List()).find((val) => {
    return val.get('sku') === annualPlanSku
  }) || Map()
  const monthlyPlan = (plans.getIn(['data', 'plans']) || List()).find((val) => {
    return val.get('sku') === monthlyPlanSku
  }) || Map()

  let annualCostPerMonth
  let monthlyCost
  switch (planType) {
    case PLAN_SUBSCRIPTION_ANNUAL:
      annualCostPerMonth = (annualPlan.getIn(['costs'], List[0]) || List()).last()
      return annualCostPerMonth
    case PLAN_SUBSCRIPTION_MONTHLY:
      monthlyCost = (monthlyPlan.getIn(['costs'], List[0]) || List()).last()
      return monthlyCost
    default:
      return null
  }
}

function getMessageVariable (
  userAccount,
  plans,
  staticText,
  messageType,
  gqlPlanData,
  gqlPlanDataLoading,
) {
  const planData = planGraphQLActive ? gqlPlanData || List() : plans || List()
  const annualPlan = planGraphQLActive ? planData.find((p) => p.get('legacyType') === PLAN_SUBSCRIPTION_ANNUAL) || Map() : Map()
  const monthlyPlan = planGraphQLActive ? planData.find((p) => p.get('legacyType') === PLAN_SUBSCRIPTION_MONTHLY) || Map() : Map()
  let annualPriceString
  let monthlyPriceString
  let percentSavingsString
  let euroSymbol
  let poundSymbol
  const currencyIso = userAccount.getIn(['details', 'data', 'billing', 'subscriptions', 'currencyIso'])
  const annualPrice = planGraphQLActive ? (annualPlan.get('costs') || List()).last() : getPricingVariable('annual', plans || List(), userAccount)
  const monthlyPrice = planGraphQLActive ? (monthlyPlan.get('costs') || List()).last() : getPricingVariable('monthly', plans || List(), userAccount)
  const currencySymbol = renderSymbol()
  const annualPercentSavings = planGraphQLActive ? annualPlan.get('percentSavings') : annualPlanPercentSavings(planData, userAccount)

  /* eslint-disable no-template-curly-in-string */
  switch (messageType) {
    case MESSAGE_TYPE_CHANGE:
    case MESSAGE_TYPE_TRIAL_UPGRADE:
      if (currencyIso === EUR) {
        euroSymbol = renderSymbol('EUR')
        annualPriceString = _replace(staticText.getIn(['data', 'changeV2', 'body1']), '${ annualPlanPrice }', `${annualPrice} ${euroSymbol}`)
        percentSavingsString = _replace(staticText.getIn(['data', 'changeV2', 'body2']), '${ percentSavings }', annualPercentSavings)
      } else if (currencyIso === GBP) {
        poundSymbol = renderSymbol('GBP')
        annualPriceString = _replace(staticText.getIn(['data', 'changeV2', 'body1']), '${ annualPlanPrice }', `${poundSymbol}${annualPrice}`)
        percentSavingsString = _replace(staticText.getIn(['data', 'changeV2', 'body2']), '${ percentSavings }', annualPercentSavings)
      } else {
        annualPriceString = _replace(staticText.getIn(['data', 'changeV2', 'body1']), '${ annualPlanPrice }', `${currencySymbol}${annualPrice}`)
        percentSavingsString = _replace(staticText.getIn(['data', 'changeV2', 'body2']), '${ percentSavings }', annualPercentSavings)
      }
      if (planGraphQLActive && !annualPlan.size && !gqlPlanDataLoading) {
        return ([staticText.getIn(['data', 'changeFallback', 'body1']), ''])
      }
      if (planGraphQLActive && !annualPlan.size && gqlPlanDataLoading) {
        return (['', ''])
      }
      return ([annualPriceString, percentSavingsString])
    case MESSAGE_TYPE_PROMO:
      if (currencyIso === EUR) {
        euroSymbol = renderSymbol('EUR')
        annualPriceString = _replace(staticText.getIn(['data', 'promo', 'body']), '${ annualPlanPrice }', `${annualPrice} ${euroSymbol}`)
      } else if (currencyIso === GBP) {
        poundSymbol = renderSymbol('GBP')
        annualPriceString = _replace(staticText.getIn(['data', 'promo', 'body']), '${ annualPlanPrice }', `${poundSymbol}${annualPrice}`)
      } else {
        annualPriceString = _replace(staticText.getIn(['data', 'promo', 'body']), '${ annualPlanPrice }', `${currencySymbol}${annualPrice}`)
      }
      if (planGraphQLActive && !annualPlan.size) {
        return ''
      }
      return annualPriceString
    case MESSAGE_TYPE_REACTIVATE:
      if (currencyIso === EUR) {
        euroSymbol = renderSymbol('EUR')
        annualPriceString = _replace(staticText.getIn(['data', 'reactivateV2', 'body1']), '${ annualPlanPrice }', `${annualPrice} ${euroSymbol}`)
        monthlyPriceString = _replace(staticText.getIn(['data', 'reactivateV2', 'body2']), '${ monthlyPlanPrice }', `${monthlyPrice} ${euroSymbol}`)
      } else if (currencyIso === GBP) {
        poundSymbol = renderSymbol('GBP')
        annualPriceString = _replace(staticText.getIn(['data', 'reactivateV2', 'body1']), '${ annualPlanPrice }', `${poundSymbol}${annualPrice}`)
        monthlyPriceString = _replace(staticText.getIn(['data', 'reactivateV2', 'body2']), '${ monthlyPlanPrice }', `${poundSymbol}${monthlyPrice}`)
      } else {
        annualPriceString = _replace(staticText.getIn(['data', 'reactivateV2', 'body1']), '${ annualPlanPrice }', `${currencySymbol}${annualPrice}`)
        monthlyPriceString = _replace(staticText.getIn(['data', 'reactivateV2', 'body2']), '${ monthlyPlanPrice }', `${currencySymbol}${monthlyPrice}`)
      }
      if (planGraphQLActive && !annualPlan.size) {
        return (['', ''])
      }
      return ([annualPriceString, monthlyPriceString])
    default:
      return null
  }
  /* eslint-enable no-template-curly-in-string */
}

const renderMessages = (props, gqlPlanData, gqlPlanDataLoading) => {
  const {
    staticText,
    optimizelyFeature,
    userAccount,
    plans,
    region,
    page,
    scheduledOrActivePause,
  } = props
  const messages = []
  const startDate = userAccount.getIn(['details', 'data', 'billing', 'subscriptions', 'startDate'])
  const date = new Date()
  const formattedStartDate = getDateTime(startDate)
  const formattedCurrentDate = getDateTime(date)
  const futureStartDate = (formattedStartDate > formattedCurrentDate)
  const locale = page.get('locale')

  // winback flow
  const isWinbackEnabled = isWinbackFlagEnabled(optimizelyFeature)

  if (showFreeTrialEndingMessage(props, locale) && region === top) {
    messages.push(
      <MyAccountMessage
        key={MESSAGE_TYPE_TRIAL_ENDING}
        type={MESSAGE_TYPE_TRIAL_ENDING}
        title={staticText.getIn(['data', 'change', 'title'])}
        message={staticText.getIn(['data', 'trial', 'ending'])}
        url={URL_ACCOUNT_CREATION}
        urlText={staticText.getIn(['data', 'trial', 'subscribe'])}
        region={region}
      />,
    )
  }

  if (showFreeTrialUpgradeMessage(props) && region === bottom) {
    messages.push(
      <MyAccountMessage
        key={MESSAGE_TYPE_TRIAL_UPGRADE}
        type={MESSAGE_TYPE_TRIAL_UPGRADE}
        title={staticText.getIn(['data', 'change', 'title'])}
        message={staticText.getIn(['data', 'trial', 'body'])}
        url={URL_ACCOUNT_CREATION}
        urlText={staticText.getIn(['data', 'trial', 'seePlans'])}
        region={region}
      />,
    )
  }

  if (
    showChangePlanUpgradeMessage(props)
    && (region === bottom && !futureStartDate)
    && !scheduledOrActivePause
  ) {
    messages.push(
      <MyAccountMessage
        key={MESSAGE_TYPE_CHANGE}
        type={MESSAGE_TYPE_CHANGE}
        title={staticText.getIn(['data', 'change', 'title'])}
        message={getMessageVariable(
          userAccount,
          plans,
          staticText,
          MESSAGE_TYPE_CHANGE,
          gqlPlanData,
          gqlPlanDataLoading,
        )}
        url={URL_ACCOUNT_CHANGE_PLAN}
        urlText={staticText.getIn(['data', 'change', 'title'])}
        region={region}
      />,
    )
  }

  if (showFmtvReactivateMessage(props) && region === bottom && !scheduledOrActivePause) {
    messages.push(
      <MyAccountMessage
        key={MESSAGE_TYPE_REACTIVATE}
        type={MESSAGE_TYPE_REACTIVATE}
        title={staticText.getIn(['data', 'reactivate', 'title'])}
        message={getMessageVariable(
          userAccount,
          plans,
          staticText,
          MESSAGE_TYPE_REACTIVATE,
          gqlPlanData,
          gqlPlanDataLoading,
        )}
        url={isWinbackEnabled ? URL_CART_ACCOUNT_CREATION_CREATE : URL_ACCOUNT_CREATION}
        urlText={staticText.getIn(['data', 'reactivate', 'details'])}
        region={region}
      />,
    )
  }

  if (showReactivateMessage(props) && region === bottom && !scheduledOrActivePause) {
    messages.push(
      <MyAccountMessage
        key={MESSAGE_TYPE_REACTIVATE}
        type={MESSAGE_TYPE_REACTIVATE}
        title={staticText.getIn(['data', 'reactivate', 'title'])}
        message={getMessageVariable(
          userAccount,
          plans,
          staticText,
          MESSAGE_TYPE_REACTIVATE,
          gqlPlanData,
          gqlPlanDataLoading,
        )}
        url={isWinbackEnabled ? URL_CART_ACCOUNT_CREATION_CREATE : URL_ACCOUNT_CREATION}
        urlText={staticText.getIn(['data', 'reactivate', 'details'])}
        region={region}
      />,
    )
  }

  if (userHasPaymentError(props) && region === top) {
    messages.push(
      <MyAccountMessage
        key={MESSAGE_TYPE_ERROR}
        type={MESSAGE_TYPE_ERROR}
        title={staticText.getIn(['data', 'error', 'title'])}
        message1={staticText.getIn(['data', 'error', 'body1'])}
        message2={staticText.getIn(['data', 'error', 'body2'])}
        url={URL_ACCOUNT}
        urlText={staticText.getIn(['data', 'errorV2', 'details'])}
        region={region}
      />,
    )
  }

  if (showUpdatePaymentMethodMessage(props) && region === top) {
    messages.push(
      <MyAccountMessage
        key={MESSAGE_TYPE_UPDATE}
        type={MESSAGE_TYPE_UPDATE}
        title={staticText.getIn(['data', 'update', 'title'])}
        message1={staticText.getIn(['data', 'updateV2', 'body1'])}
        message2={staticText.getIn(['data', 'updateV2', 'body2'])}
        url={URL_ACCOUNT}
        urlText={staticText.getIn(['data', 'updateV2', 'details'])}
        region={'top'}
      />,
    )
  }

  return messages
}

function MyAccountMessages (props) {
  const { plans, user, auth } = props
  const plansLoaded = plans.getIn(['data', 'plans'])
  const [getPlansData, { data: gqlPlanData, loading: gqlPlanDataLoading }] = useLazyQuery(GET_PLANS)
  const userLanguage = getPrimary(user.getIn(['data', 'language']))
  const [myAccountMessagesUserLanguage, setMyAccountMessagesUserLanguage] = useState(userLanguage)
  const userLanguageChanged = userLanguage !== myAccountMessagesUserLanguage
  const authJwt = auth.get('jwt')

  // get the plans for the plan grid
  useEffect(() => {
    if (planGraphQLActive) {
      getPlansData({
        variables: {
          language: userLanguage,
          planGrid: true,
          page: BILLING_PLAN_GRID_LOCATION_CHANGE_PLAN,
        },
        context: {
          jwt: authJwt,
        },
      })
    }
  }, [userLanguageChanged])

  // set the user language for this page to help handle loading plans
  // if the user changes their language while on this page
  useEffect(() => {
    setMyAccountMessagesUserLanguage(userLanguage)
  }, [userLanguage])

  if (!planGraphQLActive && !plansLoaded) {
    return null
  }

  return (
    <div className={getClassName()}>
      {renderMessages(props, fromJS(formatBillingPlans(gqlPlanData)), gqlPlanDataLoading)}
    </div>
  )
}

MyAccountMessages.propTypes = {
  plans: ImmutablePropTypes.map.isRequired,
  staticText: ImmutablePropTypes.map.isRequired,
  userAccount: ImmutablePropTypes.map.isRequired,
  auth: ImmutablePropTypes.map.isRequired,
  paytrack: ImmutablePropTypes.map.isRequired,
  scheduledOrActivePause: PropTypes.bool,
}

export default compose(
  connectRedux(
    (state) => ({
      plans: state.plans,
      language: state.user.getIn(['data', 'language', 0], 'en'),
      auth: state.auth,
      userAccount: state.userAccount,
      paytrack: state.paytrack,
      user: state.user,
      page: state.page,
      optimizelyFeature: state.optimizelyFeature,
    }),
  ),
  connectStaticText({ storeKey: 'myAccountMessages' }),
)(MyAccountMessages)
